import { has, isFunction } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';

export type Message = {
  message?: string | JSX.Element;
  title?: string;
};

export type ErrorMessage = Message & {
  /**
   * Keeps the error notification visible past the auto-dismiss timer. Unrelated to
   * SlidingPanel/Steps' `disableAutoClose`, which blocks backdrop/escape panel dismissal.
   */
  disableAutoClose?: boolean;
};

interface IProps {
  success?: Message | ((variables?, data?) => Message);
  loading?: Message | ((data) => Message);
  error?: ErrorMessage | ((data, serverError?) => ErrorMessage);
  position?: NotificationPosition;
  invalidateQueries?: string[];
  doNotClearNotification?: boolean;
}

export const useHandleErrors = ({
  success,
  loading,
  position = NotificationPosition.MODAL,
  invalidateQueries = [],
  error,
  doNotClearNotification = false,
}: IProps) => {
  const { t } = useTranslation();
  const { clearNotification, showNotification } = useNotificationContext();
  const queryClient = useQueryClient();

  const errors = {
    onSuccess: (data, variables) => {
      const response = data?.data || data;
      const isSuccess = has(response, 'success') ? response.success : true;

      const successMessage = isFunction(success) ? success(variables, data) : success;
      const title = successMessage?.title || t('titleSuccess');
      const message = successMessage?.message;

      invalidateQueries.forEach((key) => queryClient.invalidateQueries(key));

      if (isSuccess && message) {
        showNotification({
          title,
          message,
          type: ApiStatus.SUCCESS,
          position,
        });

        return;
      }

      if (!isSuccess || response?.error || response?.errors) {
        const errorMessage = isFunction(error) ? error(variables) : error;
        const title = errorMessage?.title || t('titleError');
        const message = response?.error || response?.errors || t('serverError');

        showNotification({
          title,
          message,
          type: ApiStatus.ERROR,
          position,
          disableAutoClose: errorMessage?.disableAutoClose || false,
        });

        return;
      }

      if (!message && loading && !doNotClearNotification) {
        clearNotification();
      }
    },

    onMutate: (data) => {
      const loadingMessage = isFunction(loading) ? loading(data) : loading;

      if (!loadingMessage?.message) {
        return;
      }

      showNotification({
        title: loadingMessage?.title || t('titleStatus'),
        message: loadingMessage?.message,
        type: ApiStatus.LOADING,
        position,
      });
    },

    onError: (serverError: any, variables) => {
      const errorMessage = isFunction(error)
        ? error(variables, serverError?.response?.data)
        : error;

      showNotification({
        title: errorMessage?.title || t('titleError'),
        message: errorMessage?.message || serverError.response?.data?.error || t('serverError'),
        type: ApiStatus.ERROR,
        position,
        disableAutoClose: errorMessage?.disableAutoClose || false,
      });
    },
  };

  return { errors };
};
